import { Alert, Button, Checkbox } from '@design-system';
import { AddressElement, PaymentElement } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { Trans, useTranslation } from 'next-i18next';

import { StripeAddressDetails } from '../../../../features/draws/types';
import { useAnalyticsEmitter } from '../../../../utils/analytics/emitter';

interface Props {
  error: string | undefined;
  isBillingSameAsShipping: boolean;
  billingAddress: StripeAddressDetails | null;
  setBillingAddress: (address: StripeAddressDetails | null) => void;
  setIsBillingSameAsShipping: (isBillingSameAsShipping: boolean) => void;
  onSave: () => void;
  onPaymentElementChange?: (event: StripePaymentElementChangeEvent) => void;
}

export const AddPaymentMethod = ({
  billingAddress,
  error,
  isBillingSameAsShipping,
  onPaymentElementChange,
  onSave,
  setBillingAddress,
  setIsBillingSameAsShipping,
}: Props) => {
  const { t } = useTranslation(['product', 'countries']);
  const [emitter] = useAnalyticsEmitter();

  const getPaymentError = () => {
    if (!error) return '';

    const genericErrorCode = 'drawers.payment.paymentErrors.genericError';
    const paymentErrorMappingCode = `drawers.payment.paymentErrors.stripeErrorMapping.${error}`;

    const paymentErrorMapping = t(paymentErrorMappingCode);

    if (paymentErrorMapping === paymentErrorMappingCode) {
      return (
        <Trans i18nKey={genericErrorCode} t={t}>
          Card authentication failed, try again or&nbsp;
          <a className="hyperlink" href="mailto:support@avantarte.com">
            get in touch
          </a>
          .
        </Trans>
      );
    }

    return paymentErrorMapping;
  };

  return (
    <div className="flex flex-col space-y-6">
      {error && <Alert status="error">{getPaymentError()}</Alert>}
      <div className="flex flex-col space-y-2">
        <PaymentElement
          onChange={onPaymentElementChange}
          options={{
            fields: {
              billingDetails: 'never',
            },
            wallets: {
              applePay: 'never',
              googlePay: 'never',
              link: 'never',
            },
            layout: 'tabs',
            terms: {
              card: 'never',
            },
            business: {
              name: 'Avant Arte',
            },
          }}
        />
        <div className="text-xs text-neutral-4">
          {t('drawers.payment.disclaimer')}
        </div>
      </div>
      <div className="flex flex-col space-y-6">
        <Checkbox
          checked={isBillingSameAsShipping}
          disabled={false}
          id="use-shipping-address"
          label={t('drawers.payment.billingAddressButton')}
          name="use-shipping-address"
          onChange={(e) => {
            const { checked } = e.currentTarget;

            if (checked) setBillingAddress(null);
            setIsBillingSameAsShipping(checked);
          }}
          type="checkbox"
          value="use-shipping-address"
        />

        {!isBillingSameAsShipping && (
          <AddressElement
            onChange={(event) => {
              setBillingAddress(event.value);
            }}
            options={{
              mode: 'billing',
              autocomplete: {
                mode: 'automatic',
              },
              display: {
                name: 'full',
              },
              fields: {
                phone: 'always',
              },
              validation: {
                phone: {
                  required: 'always',
                },
              },
            }}
          />
        )}
      </div>
      <Button
        disabled={!isBillingSameAsShipping && !billingAddress}
        fullWidth
        label={t('drawers.payment.saveButton')}
        onClick={() => {
          emitter('new details', 'saved', {
            'billing same as shipping address': isBillingSameAsShipping,
          });
          onSave();
        }}
        variant="primary"
      />
    </div>
  );
};
